"use client";

import {
    CalendarCheck,
    DoorOpen,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/components/LanguageContext";

import {
    minBookingGuests,
    maxBookingGuests,
} from "@/lib/privateRoomConfig";

const translations = {
    hu: {
        heroTitle:
            "private room",

        bookingButton:
            "private room foglalás",

        firstImageAlt:
            "strike private room különterem születésnapra és csapatépítőre Budapesten",

        descriptionTitle:
            "CSAK A TIÉTEK",

        descriptionFirst: (
            <>
                a <strong className="font-bold text-black">private room</strong> a STRIKE exkluzív különterme, ahol a játék és a tér is csak a tiétek.{" "}
                <strong className="font-bold text-black">minimum {minBookingGuests}, maximum {maxBookingGuests} fős társaságoknak</strong> alakítottuk ki, hogy zavartalanul játszhassatok, ünnepelhessetek és együtt lehessetek.
            </>
        ),

        descriptionSecond: (
            <>
                legyen szó <strong className="font-bold text-black">születésnapról, céges csapatépítőről, baráti összejövetelről vagy bármilyen különleges alkalomról</strong>, itt saját térben élhetitek át a STRIKE élményt.
            </>
        ),

        descriptionThird: (
            <>
                a foglalás teljes időtartama alatt <strong className="font-bold text-black">a különterem és a benne található játékok kizárólag a ti társaságotoké.</strong>
            </>
        ),

        descriptionMotto:
            "saját tér. saját játék. saját élmény. 🎯",

        descriptionClosing: (
            <>játsszatok, versenyezzetek, ünnepeljetek - <strong className="font-bold text-black">itt most tényleg csak ti vagytok.</strong></>
        ),

        partySubtitle:
            "minden, ami egy közös ünnepléshez kell",

        partyFeatures: [
            "90 perc privát élmény",
            "60 perc strike zone játékidő + 30 perc private room használat",
            "privát különterem használata 90 percen át",
            "1 üdítő / fő",
            "1 melegszendvics / fő",
            "ünnepi előkészítés",
            "rex asztal használata",
        ],

        partyPrice:
            "8 900 Ft / fő",

        secondImageAlt:
            "játékok a strike private room különteremben Budapesten",

        xlSubtitle:
            "hosszabb játékidő, még teljesebb élmény",

        xlFeatures: [
            "180 perc privát strike élmény",
            "2 x 60 perc játékidő + összesen 60 perc szünet és pihenés",
            "privát különterem használata 180 percen át",
            "1 üdítő / fő",
            "1 melegszendvics / fő",
            "1 közös tálca chips",
            "1 pohár alkoholmentes pezsgő / fő",
            "ünnepi előkészítés",
            "rex asztal használata",
        ],

        xlPrice:
            "14 900 Ft / fő",

        thirdImageAlt:
            "strike private room privát különterem Budapesten",

        conditionsTitle:
            "foglalási feltételek",

        conditionsIntroduction:
            "a foglalás előtt kérjük, olvassátok el az alábbi fontos tudnivalókat.",

        bookingConditions: [
            `a private room minimum ${minBookingGuests}, maximum ${maxBookingGuests} fő részére foglalható.`,
            "a különterem és a hozzá tartozó játékok kizárólag előzetesen lefoglalt időpontban vehetők igénybe.",
            "a foglalás véglegesítéséhez teljes előrefizetés szükséges.",
            "kérjük, érkezzetek legalább 15 perccel a lefoglalt időpont előtt.",
            "a foglalás az időpont előtt legkésőbb 72 órával módosítható díjmentesen.",
        ],

        priceLabel:
            "ár",

        minimumBooking:
            "minimum foglalás:",

        maximumGuests:
            "maximum létszám:",

        minimumGuests:
            `${minBookingGuests} fő`,

        maximumBookingGuests:
            `${maxBookingGuests} fő`,
    },

    en: {
        heroTitle:
            "private room",

        bookingButton:
            "book a private room",

        firstImageAlt:
            "strike private room for birthdays and team building in Budapest",

        descriptionTitle:
            "ALL YOURS",

        descriptionFirst: (
            <>
                the <strong className="font-bold text-black">private room</strong> is STRIKE’s exclusive room, where the space and games are all yours. designed for <strong className="font-bold text-black">groups of {minBookingGuests} to {maxBookingGuests}</strong>, it lets you play, celebrate and spend time together without interruptions.
            </>
        ),

        descriptionSecond: (
            <>
                whether it is <strong className="font-bold text-black">a birthday, corporate team building, a gathering with friends or any special occasion</strong>, enjoy the STRIKE experience in your own space.
            </>
        ),

        descriptionThird: (
            <>
                throughout your booking, <strong className="font-bold text-black">the private room and the games inside are exclusively yours.</strong>
            </>
        ),

        descriptionMotto:
            "your space. your games. your experience. 🎯",

        descriptionClosing: (
            <>play, compete and celebrate - <strong className="font-bold text-black">this time, it really is just you.</strong></>
        ),

        partySubtitle:
            "everything you need for a shared celebration",

        partyFeatures: [
            "90-minute private experience",
            "60 minutes of strike zone playtime + 30 minutes in the private room",
            "exclusive use of the private room for 90 minutes",
            "1 soft drink per person",
            "1 toasted sandwich per person",
            "celebration setup",
            "use of the rex table",
        ],

        partyPrice:
            "8,900 HUF / person",

        secondImageAlt:
            "games inside the strike private room in Budapest",

        xlSubtitle:
            "more playtime for an even bigger experience",

        xlFeatures: [
            "180-minute private strike experience",
            "2 x 60 minutes of playtime + 60 minutes for breaks and relaxation in total",
            "exclusive use of a private room for 180 minutes",
            "1 soft drink per person",
            "1 toasted sandwich per person",
            "1 shared tray of crisps",
            "1 glass of alcohol-free sparkling wine per person",
            "celebration setup",
            "use of the rex table",
        ],

        xlPrice:
            "14,900 HUF / person",

        thirdImageAlt:
            "strike private room venue in Budapest",

        conditionsTitle:
            "booking conditions",

        conditionsIntroduction:
            "please read the following important information before booking.",

        bookingConditions: [
            `the private room can be booked for a minimum of ${minBookingGuests} and a maximum of ${maxBookingGuests} guests.`,
            "the private room and its games are available exclusively with a prior reservation.",
            "a deposit or full advance payment is required to confirm the booking.",
            "please arrive at least 15 minutes before your reserved time.",
            "the booking can be modified free of charge up to 72 hours before the reserved time.",
        ],

        priceLabel:
            "price",

        minimumBooking:
            "minimum booking:",

        maximumGuests:
            "maximum group size:",

        minimumGuests:
            `${minBookingGuests} guests`,

        maximumBookingGuests:
            `${maxBookingGuests} guests`,
    },
};

export default function PrivateRoomPage() {
    const {
        language,
    } =
        useLanguage();

    const text =
        translations[language] ||
        translations.hu;

    return (
        <main className="min-h-screen overflow-x-hidden bg-black font-sans">
            <Header />

            {/* OLDAL TETEJE */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto flex min-h-[380px] w-full max-w-[430px] flex-col items-center justify-center px-7 py-12 text-center md:max-w-5xl md:min-h-[500px] md:px-12 md:py-16">
                    <div
                        className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
                        style={{
                            width:
                                "80px",

                            height:
                                "80px",

                            minWidth:
                                "80px",

                            maxWidth:
                                "80px",

                            minHeight:
                                "80px",

                            maxHeight:
                                "80px",

                            boxSizing:
                                "border-box",
                        }}
                    >
                        <DoorOpen
                            style={{
                                width:
                                    "40px",

                                height:
                                    "40px",
                            }}
                            strokeWidth={
                                2.5
                            }
                        />
                    </div>

                    <p className="text-sm font-bold tracking-[0.15em] text-neutral-600 md:text-base">{text.heroTitle}</p>
                    <h1 className="mt-3 text-[36px] font-black uppercase leading-tight md:text-[58px]">
                        {text.descriptionTitle}
                    </h1>

                    <div className="mt-7 max-w-3xl space-y-5 text-base leading-relaxed text-neutral-700 md:text-xl">
                        <p>{text.descriptionFirst}</p>
                        <p>{text.descriptionSecond}</p>
                        <p>{text.descriptionThird}</p>
                        <p className="text-lg font-black text-black md:text-2xl">{text.descriptionMotto}</p>
                        <p>{text.descriptionClosing}</p>
                    </div>

                    <a
                        href="/private-room/booking"
                        className="
                            button-pulse mt-9 flex h-[52px]
                            min-w-[240px]
                            cursor-pointer select-none
                            items-center justify-center
                            rounded-[6px]
                            bg-[#f5a400] px-7
                            text-[18px] font-black
                            text-black transition-transform
                            duration-200
                            hover:scale-[1.03]
                            active:translate-y-[2px]
                            active:scale-95
                            md:mt-11 md:h-[60px]
                            md:min-w-[280px]
                            md:text-[21px]
                        "
                    >
                        {text.bookingButton}
                    </a>
                </div>
            </section>

            <PrivateRoomImage
                src="/v4/megerkeztel_strike.webp"
                alt={
                    text.firstImageAlt
                }
            />

            {/* PRIVATE ROOM PARTY */}
            <PackageSection
                label="01"
                title="private room party"
                subtitle={
                    text.partySubtitle
                }
                features={
                    text.partyFeatures
                }
                price={
                    text.partyPrice
                }
                backgroundClass="bg-[#f2f2f2]"
                text={
                    text
                }
            />

            <PrivateRoomImage
                src="/v4/private_room_strike_v4.webp"
                alt={
                    text.secondImageAlt
                }
            />

            {/* PRIVATE ROOM XL */}
            <PackageSection
                label="02"
                title="private room xl"
                subtitle={
                    text.xlSubtitle
                }
                features={
                    text.xlFeatures
                }
                price={
                    text.xlPrice
                }
                backgroundClass="bg-[#dedede]"
                text={
                    text
                }
            />

            <PrivateRoomImage
                src="/v4/private_room2_strike.webp"
                alt={
                    text.thirdImageAlt
                }
            />

            {/* FOGLALÁSI FELTÉTELEK */}
            <section className="bg-[#dedede] text-black">
                <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                    <div className="border-[3px] border-black bg-[#f2f2f2] px-5 py-10 md:px-12 md:py-14">
                        <div className="flex flex-col items-center text-center">
                            <div
                                className="mb-6 flex shrink-0 items-center justify-center rounded-full border-[3px] border-black"
                                style={{
                                    width:
                                        "80px",

                                    height:
                                        "80px",

                                    minWidth:
                                        "80px",

                                    maxWidth:
                                        "80px",

                                    minHeight:
                                        "80px",

                                    maxHeight:
                                        "80px",

                                    boxSizing:
                                        "border-box",
                                }}
                            >
                                <CalendarCheck
                                    style={{
                                        width:
                                            "38px",

                                        height:
                                            "38px",
                                    }}
                                    strokeWidth={
                                        2.5
                                    }
                                />
                            </div>

                            <h2 className="text-[22px] font-black lowercase leading-none md:text-[40px]">
                                {text.conditionsTitle}
                            </h2>

                            <p className="mt-6 max-w-[300px] text-[15px] leading-[1.3] text-neutral-700 md:max-w-xl md:text-[19px] md:leading-[1.45]">
                                {text.conditionsIntroduction}
                            </p>
                        </div>

                        <div className="mt-10 border-y-[3px] border-black md:mt-14">
                            {text.bookingConditions.map(
                                (
                                    condition,
                                    index
                                ) => (
                                    <div
                                        key={
                                            condition
                                        }
                                        className={`flex items-start gap-4 py-5 md:gap-6 md:py-7 ${index !==
                                            text.bookingConditions.length - 1
                                            ? "border-b border-black/25"
                                            : ""
                                            }`}
                                    >
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black text-[11px] font-black text-white md:h-11 md:w-11 md:text-[13px]">
                                            {String(
                                                index +
                                                1
                                            ).padStart(
                                                2,
                                                "0"
                                            )}
                                        </span>

                                        <p className="pt-1 text-[15px] leading-[1.4] text-neutral-700 md:pt-2 md:text-[19px] md:leading-[1.5]">
                                            {condition}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>

                        <div className="mt-10 flex justify-center md:mt-14">
                            <a
                                href="/private-room/booking"
                                className="
                                    button-pulse flex h-[48px]
                                    min-w-[230px]
                                    cursor-pointer
                                    select-none
                                    items-center
                                    justify-center
                                    overflow-hidden
                                    whitespace-nowrap
                                    rounded-[6px]
                                    bg-[#f5a400]
                                    px-6
                                    text-[17px]
                                    font-black
                                    text-black
                                    transition-transform
                                    duration-200
                                    ease-out
                                    hover:scale-[1.02]
                                    active:translate-y-[2px]
                                    active:scale-95
                                    [-webkit-touch-callout:none]
                                    md:text-[20px]
                                    md:hover:scale-105
                                "
                            >
                                {text.bookingButton}
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}

function PackageSection({
    label,
    title,
    subtitle,
    features,
    price,
    backgroundClass,
    text,
}) {
    return (
        <section className={`${backgroundClass} text-black`}>
            <div className="mx-auto w-full max-w-[430px] px-5 py-16 md:max-w-5xl md:px-10 md:py-24">
                <div className="border-[3px] border-black bg-[#f2f2f2]">
                    <div className="bg-black px-6 py-8 text-center text-white md:px-12 md:py-12">
                        <span className="text-[12px] font-black tracking-[0.18em] text-white/50 md:text-[14px]">
                            {label}
                        </span>

                        <h2 className="mt-3 text-[25px] font-black lowercase leading-none md:text-[42px]">
                            {title}
                        </h2>

                        <p className="mx-auto mt-5 max-w-[290px] text-[14px] leading-[1.3] text-white/70 md:max-w-xl md:text-[19px]">
                            {subtitle}
                        </p>
                    </div>

                    <div className="px-5 py-8 md:px-12 md:py-12">
                        <div className="border-y-[3px] border-black">
                            {features.map(
                                (
                                    feature,
                                    index
                                ) => (
                                    <div
                                        key={
                                            feature
                                        }
                                        className={`flex items-center gap-4 py-4 md:gap-6 md:py-5 ${index !==
                                            features.length - 1
                                            ? "border-b border-black/25"
                                            : ""
                                            }`}
                                    >
                                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black text-[10px] font-black text-white md:h-10 md:w-10 md:text-[12px]">
                                            {String(
                                                index +
                                                1
                                            ).padStart(
                                                2,
                                                "0"
                                            )}
                                        </span>

                                        <p className="text-[15px] font-semibold leading-[1.25] text-neutral-700 md:text-[19px]">
                                            {feature}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>

                        <div className="mt-8 text-center md:mt-10">
                            <p className="text-[13px] font-black lowercase tracking-[0.14em] text-neutral-500 md:text-[15px]">
                                {text.priceLabel}
                            </p>

                            <p className="mt-2 text-[28px] font-black leading-none text-black md:text-[42px]">
                                {price}
                            </p>

                            <div className="mt-7 text-[15px] leading-[1.4] text-neutral-700 md:text-[19px]">
                                <p>
                                    {text.minimumBooking}{" "}
                                    <strong className="font-black text-black">
                                        {text.minimumGuests}
                                    </strong>
                                </p>

                                <p>
                                    {text.maximumGuests}{" "}
                                    <strong className="font-black text-black">
                                        {text.maximumBookingGuests}
                                    </strong>
                                </p>
                            </div>

                            <a
                                href="/private-room/booking"
                                className="
                                    button-pulse mx-auto mt-8 flex
                                    h-[52px] w-full
                                    max-w-[300px]
                                    cursor-pointer select-none
                                    items-center justify-center
                                    rounded-[6px]
                                    bg-[#f5a400] px-7
                                    text-[18px] font-black
                                    text-black transition-transform
                                    duration-200
                                    hover:scale-[1.03]
                                    active:translate-y-[2px]
                                    active:scale-95
                                    md:mt-10 md:h-[60px]
                                    md:text-[21px]
                                "
                            >
                                {text.bookingButton}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function PrivateRoomImage({
    src,
    alt,
}) {
    return (
        <section className="w-full overflow-hidden bg-black">
            <div className="relative flex w-full items-center justify-center overflow-hidden bg-black">
                <div
                    aria-hidden="true"
                    className="absolute inset-0 scale-[1.15] bg-cover bg-center blur-[45px]"
                    style={{
                        backgroundImage:
                            `url("${src}")`,
                    }}
                />

                <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-black/20"
                />

                <img
                    src={
                        src
                    }
                    alt={
                        alt
                    }
                    className="relative z-10 block h-auto w-full max-w-[900px] object-contain"
                />
            </div>
        </section>
    );
}